/**
 * ContactSupportForm
 *
 * Authenticated form that submits a support message to the server. Includes a
 * transient success toast below the submit button.
 */
import React, { useState, useEffect } from 'react';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';
import { Textarea } from '../../ui/inputs/textarea';
import { Label } from '../../ui/inputs/label';

export default function ContactSupportForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);

  // Add useEffect hook to clear success message after a few seconds
  useEffect(() => {
    let timer;
    if (submitStatus && submitStatus.success) {
      timer = setTimeout(() => {
        setSubmitStatus(null);
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [submitStatus]);

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Get the current user's ID token for authentication
      const idToken = await auth.currentUser.getIdToken();
      
      const response = await fetch(`${SERVER_URL}/contact/auth`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send message');
      }

      const data = await response.json();
      setSubmitStatus({ 
        success: true, 
        message: data.message || 'Your message has been sent successfully.' 
      });
      setMessage('');
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSubmitStatus({ 
        success: false, 
        message: error.message || 'Failed to send message. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-10 grid max-w-7xl grid-cols-1 gap-x-8 md:grid-cols-[300px,1fr]">
      <div className="py-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Contact Support</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Get in touch with our support team for personalized assistance.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Remove success message from here */}
          {submitStatus && !submitStatus.success && (
            <div className="p-4 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/50 dark:text-red-300">
              {submitStatus.message}
            </div>
          )}

          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              name="message"
              value={message}
              onChange={handleMessageChange}
              required
              rows={6}
              className="mt-2"
              placeholder="Please describe your issue or question in detail..."
            />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Your account information will be automatically included with your message.
            </p>
          </div>

          <div className="flex flex-col items-start space-y-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Support Request'}
            </button>
            
            {/* Success message below the button with green background */}
            {submitStatus && submitStatus.success && (
              <div className="text-xs rounded-md px-3 py-2 bg-green-50 text-green-800 dark:bg-green-900/50 dark:text-green-300 mt-2 animate-fade-in">
                {submitStatus.message}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}